import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { type WorkspaceMemberStore } from '@/features/iam/members/WorkspaceMemberStore'
import { type GetCurrentMemberFn, type GetCurrentUserFn } from '@/features/iam'
import { func, required, validationContext } from 'validx'
import { containsOnlyValidPhoneNumberChars } from '@/utils/phone-number-util'
import { type SnackbarFactory } from '@/features/shell'

/**
 * View model for the profile editor.
 */
export class EditProfileViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor phone = ''

  // We don't currently allow modifying the email.
  @observable accessor email = ''

  readonly validation = validationContext(this, {
    givenName: [required('First name is required.')],
    familyName: [required('Last name is required.')],
    phone: [
      required('Phone number is required.'),
      func(
        () =>
          containsOnlyValidPhoneNumberChars(this.phone) ||
          'Phone number is invalid',
      ),
    ],
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _memberStore: WorkspaceMemberStore,
    private readonly _newSnackbar: SnackbarFactory,
  ) {
    this.modal.show$.subscribe(this._reset)
  }

  /**
   * Opens the profile editor.
   */
  @action.bound
  open() {
    this.modal.show()
  }

  /**
   * Saves the profile.
   */
  save = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const snackbar = this._newSnackbar((s) =>
      s
        .saying('Success!', 'Your profile has been updated.')
        .type('success')
        .autoDismiss(),
    )

    try {
      await this._memberStore.updateProfile(this._getCurrentMember(), {
        givenName: this.givenName,
        familyName: this.familyName,
        phone: this.phone,
      })
      snackbar.show()
      this.modal.close()
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })

  @action.bound
  private _reset() {
    const user = this._getCurrentUser()
    this.givenName = user.givenName
    this.familyName = user.familyName
    this.email = user.email ?? ''
    this.phone = user.phone ?? ''
  }
}
